export type PageAccessMode = 'manage' | 'read-only' | 'deny';

const normalizeRole = (role: string): string => role.trim().toLowerCase();

const hasAnyRole = (availableRoles: string[], requiredRoles: string[]): boolean => {
  if (!requiredRoles.length) {
    return true;
  }

  const normalizedAvailable = new Set(availableRoles.map(normalizeRole));
  return requiredRoles.some((role) => normalizedAvailable.has(normalizeRole(role)));
};

type RouteAccessRule = {
  path: string;
  allowedRoles: string[];
  teacherReadOnly?: boolean;
};

const routeAccessRules: RouteAccessRule[] = [
  { path: '/teacher-dashboard', allowedRoles: ['superadmin', 'owner', 'principal', 'teacher', 'staff', 'admin'] },
  { path: '/manage-assignments', allowedRoles: ['superadmin', 'owner', 'principal', 'teacher', 'staff', 'admin'] },
  { path: '/manage-submissions', allowedRoles: ['superadmin', 'owner', 'principal', 'teacher', 'staff', 'admin'] },
  { path: '/manage-exams', allowedRoles: ['superadmin', 'owner', 'principal', 'teacher', 'staff', 'admin'] },
  { path: '/manage-schedule', allowedRoles: ['superadmin', 'owner', 'principal', 'teacher', 'staff', 'admin'] },
  { path: '/manage-sessions', allowedRoles: ['superadmin', 'owner', 'principal', 'teacher', 'staff', 'admin'] },
  { path: '/manage-categories-v2', allowedRoles: ['superadmin', 'owner', 'principal', 'teacher', 'staff', 'admin'] },
  { path: '/manage-questions', allowedRoles: ['superadmin', 'owner', 'principal', 'teacher', 'staff', 'admin'] },
  { path: '/manage-teacher-student-links', allowedRoles: ['superadmin', 'owner', 'principal', 'teacher', 'staff', 'admin'] },
  { path: '/manage-teaching-assignments', allowedRoles: ['superadmin', 'owner', 'principal', 'teacher', 'staff', 'admin'] },
  { path: '/manage-teacher-course-assignments', allowedRoles: ['superadmin', 'owner', 'principal', 'teacher', 'staff', 'admin'] },
  { path: '/manage-staff', allowedRoles: ['superadmin', 'owner', 'principal', 'staff'] },
  { path: '/manage-teachers', allowedRoles: ['superadmin', 'owner', 'principal', 'staff'] },
  { path: '/manage-students', allowedRoles: ['superadmin', 'owner', 'principal', 'staff', 'teacher'], teacherReadOnly: true },
  { path: '/manage-organization-memberships', allowedRoles: ['superadmin', 'owner'] },
  { path: '/manage-organization-members-and-roles', allowedRoles: ['superadmin', 'owner'] },
  { path: '/manage-organization-membership-roles', allowedRoles: ['superadmin', 'owner'] },
  { path: '/manage-organization-requests', allowedRoles: ['superadmin', 'owner', 'principal'] },
  { path: '/manage-platform-user-roles', allowedRoles: ['superadmin'] },
  { path: '/super-admin/platform-users', allowedRoles: ['superadmin'] },
  { path: '/super-admin', allowedRoles: ['superadmin'] },
];

export const isAdminRoute = (pathname: string) =>
  pathname === '/admin' ||
  pathname === '/super-admin' ||
  pathname.startsWith('/super-admin/') ||
  pathname.startsWith('/manage-') ||
  pathname.startsWith('/admin/') ||
  pathname === '/books' ||
  pathname === '/reports' ||
  pathname.startsWith('/reports/');

const getMatchingRouteRule = (pathname: string): RouteAccessRule | undefined => {
  return routeAccessRules.find((rule) => pathname === rule.path || pathname.startsWith(`${rule.path}/`));
};

export const isPathAllowedForRoles = (pathname: string, roles: string[]): boolean => {
  const rule = getMatchingRouteRule(pathname);
  if (!rule) {
    return false;
  }

  return hasAnyRole(roles, rule.allowedRoles);
};

export const getPageAccessMode = (pathname: string, roles: string[]): PageAccessMode => {
  const rule = getMatchingRouteRule(pathname);
  if (!rule || !hasAnyRole(roles, rule.allowedRoles)) {
    return 'deny';
  }

  const isTeacherRestricted = rule.teacherReadOnly && hasAnyRole(roles, ['teacher']) && !hasAnyRole(roles, ['superadmin', 'owner', 'principal', 'staff', 'admin']);
  return isTeacherRestricted ? 'read-only' : 'manage';
};

export const shouldRedirectAdminAccess = (pathname: string): boolean => {
  if (!isAdminRoute(pathname)) {
    return false;
  }

  const rule = getMatchingRouteRule(pathname);
  return !rule;
};
